import React from 'react';
import { Shuffle, Star, Delete } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface QuickPicksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  type?: 'quick' | 'fav' | 'clear';
}

/**
 * Centered confirmation dialog for Quick Pick, Fav Picks, and Clear Picks.
 *
 * Uses shadcn DialogContent which portals itself to document.body and renders
 * a single built-in close button, keeping it centered in the iframe viewport
 * regardless of scroll position.
 */
const QuickPicksModal: React.FC<QuickPicksModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Quick Pick',
  description = 'Randomly select outcomes for your remaining selections?',
  confirmText = 'Yes, add quick picks',
  type = 'quick',
}) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        data-test-id="quick-picks-modal"
        className="bg-[#111111] border-0 rounded-2xl p-0 overflow-hidden shadow-xl w-[90vw] max-w-sm [&>button]:text-white [&>button]:opacity-80 [&>button]:hover:opacity-100"
      >
        {/* Header */}
        <div className="flex items-center justify-center px-6 pt-6 pb-4">
          <DialogTitle className="text-white text-xl font-bold">{title}</DialogTitle>
        </div>

        {/* Body */}
        <div className="px-6 pb-6">
          <div className="bg-[#1a1a1a] rounded-xl p-6 mb-6">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center justify-center bg-[#525252] rounded-sm p-2">
                {type === 'fav' ? (
                  <Star className="w-12 h-12 text-white fill-white" />
                ) : type === 'clear' ? (
                  <Delete className="w-12 h-12 text-white" />
                ) : (
                  <Shuffle className="w-12 h-12 text-white" />
                )}
              </div>
              <p className="text-white text-center text-sm leading-relaxed">{description}</p>
            </div>
          </div>

          <Button
            data-test-id="confirm-btn"
            variant="pickem"
            onClick={handleConfirm}
            className="w-full bg-[#2a2a2a] hover:bg-[#353535] text-white font-semibold py-3 rounded-[8px] border-0"
          >
            {confirmText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickPicksModal;
